import { useEffect, useRef } from "react";

import { resolveBuffered } from "../../components/player-ui/helpers";
import { LIVE_BUFFER_IN_SECONDS } from "../../constants";
import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { usePlayerStore } from "../../store/usePlayerStore";

const getSeekableEnd = (video: HTMLMediaElement) => {
  return video?.seekable.length
    ? Math.round(video?.seekable.end(0) - LIVE_BUFFER_IN_SECONDS)
    : 0;
};

const isAtEnd = (video: HTMLMediaElement) => {
  return video.currentTime >= video.duration;
};

const useBindVideoEventListeners = (video?: HTMLVideoElement): void => {
  const { useStoreState } = usePlayerStore();

  const snowplowEvents = useStoreState.snowplowEvents();
  const device = useStoreState.device();
  const errorOverlayVisible = useStoreState.errorOverlayVisible();
  const externalEvents = useStoreState.externalEvents();
  const isCasting = useStoreState.isCasting();
  const isLive = useStoreState.isLive();

  const setBuffered = useStoreState.setBuffered();
  const setCurrentTime = useStoreState.setCurrentTime();
  const setError = useStoreState.setError();
  const setIsEnded = useStoreState.setIsEnded();
  const setIsFullscreen = useStoreState.setIsFullscreen();
  const setIsLoading = useStoreState.setIsLoading();
  const setIsMuted = useStoreState.setIsMuted();
  const setIsPaused = useStoreState.setIsPaused();
  const setIsPictureInPicture = useStoreState.setIsPictureInPicture();
  const setIsPlaybackStarted = useStoreState.setIsPlaybackStarted();
  const setIsSeeking = useStoreState.setIsSeeking();
  const setPlaybackRate = useStoreState.setPlaybackRate();
  const setSeekableEnd = useStoreState.setSeekableEnd();
  const setVolume = useStoreState.setVolume();

  const { loop } = useEmbedParametersContext();

  const isBuffering = useRef(false);

  useEffect(() => {
    if (video && !isCasting && externalEvents) {
      const onVolumeChanged = (event: Event) => {
        const target = event.target as HTMLVideoElement;
        setIsMuted(target.muted);
        setVolume(target.volume);
        snowplowEvents?.onVolumeChange(target.muted ? 0 : target.volume * 100);
      };

      const onRateChange = (event: Event) => {
        const rate = (event.target as HTMLVideoElement).playbackRate;
        setPlaybackRate(rate);
        snowplowEvents?.onPlaybackRateChange(rate);
      };

      const onEnded = () => {
        setIsEnded(true);
        snowplowEvents?.onFinish();
      };

      const onPause = () => {
        setIsPaused(true);
        snowplowEvents?.onPause();
      };

      const onPlay = () => {
        setIsPaused(false);
        setIsEnded(false);
        setIsPlaybackStarted(true);
        setError(undefined);
        snowplowEvents?.onPlay();
      };

      const onPipEnter = () => {
        setIsPictureInPicture(true);
        externalEvents.dispatch("onPipModeChange");
        snowplowEvents?.onPipChange(true);
      };

      const onPipLeave = () => {
        setIsPictureInPicture(false);
        externalEvents.dispatch("onPipModeChange");
        snowplowEvents?.onPipChange(false);
      };

      const onPlaying = () => {
        setIsLoading(false);
        externalEvents.dispatch("playing");
        if (isBuffering.current) {
          snowplowEvents?.onBufferEnd();
          isBuffering.current = false;
        }
      };

      const onProgress = (event: Event) => {
        const v = event.target as HTMLMediaElement;
        setBuffered(
          resolveBuffered({
            buffered: v.buffered,
            currentTime: v.currentTime
          })
        );
        if (isLive) {
          setSeekableEnd(getSeekableEnd(v));
        }
      };

      const onTimeUpdate = (event: Event) => {
        const mediaElement = event.target as HTMLMediaElement;
        setCurrentTime(mediaElement.currentTime);
        snowplowEvents?.onTimeUpdate(
          (event.target as HTMLMediaElement).currentTime
        );
        externalEvents.dispatch("onTimeUpdate");

        // For some reason, the loop does not always work on safari 18 or iOS 18, the video gets stuck at the end of the video.
        // This might happen when current time is same or greater than duration. In working cases the current time is always less than the duration.
        // Force looping by setting the current time back to 0 at the end of the video.
        // TODO: Remove if this possible bug is fixed for example in Hls.js
        if (
          (device.browser.name === "Safari" ||
            device.browser.name === "Mobile Safari" ||
            device.os.name === "iOS") &&
          loop &&
          isAtEnd(mediaElement)
        ) {
          mediaElement.currentTime = 0;
        }
      };

      const onSeeking = () => {
        setIsSeeking(false);
        setIsSeeking(true);
        snowplowEvents?.onSeekStart();
      };

      const onSeekEnd = () => {
        setIsSeeking(false);
        snowplowEvents?.onSeekEnd();
      };

      const onWaiting = () => {
        if (!errorOverlayVisible) {
          setIsLoading(true);
          snowplowEvents?.onBufferStart();
          isBuffering.current = true;
        }
      };

      const webkitBeginFullscreen = () => {
        setIsFullscreen(true);
        snowplowEvents?.onFullscreenChange(true);
      };
      const webkitEndFullscreen = () => {
        setIsFullscreen(false);
        snowplowEvents?.onFullscreenChange(false);
      };

      const events = [
        { event: "canplay", fn: () => setIsLoading(false) },
        { event: "ended", fn: onEnded },
        { event: "enterpictureinpicture", fn: onPipEnter },
        { event: "error", fn: setError },
        { event: "leavepictureinpicture", fn: onPipLeave },
        { event: "pause", fn: onPause },
        { event: "play", fn: onPlay },
        { event: "playing", fn: onPlaying },
        { event: "progress", fn: onProgress },
        { event: "ratechange", fn: onRateChange },
        { event: "seeking", fn: onSeeking },
        { event: "seeked", fn: onSeekEnd },
        { event: "timeupdate", fn: onTimeUpdate },
        { event: "volumechange", fn: onVolumeChanged },
        { event: "waiting", fn: onWaiting },
        { event: "webkitbeginfullscreen", fn: webkitBeginFullscreen },
        { event: "webkitendfullscreen", fn: webkitEndFullscreen }
      ];

      events.forEach(({ event, fn }) => video.addEventListener(event, fn));

      return () => {
        events.forEach(({ event, fn }) => video.removeEventListener(event, fn));
      };
    }
  }, [
    device,
    errorOverlayVisible,
    externalEvents,
    isCasting,
    isLive,
    loop,
    setBuffered,
    setCurrentTime,
    setError,
    setIsEnded,
    setIsFullscreen,
    setIsLoading,
    setIsMuted,
    setIsPaused,
    setIsPictureInPicture,
    setIsPlaybackStarted,
    setIsSeeking,
    setPlaybackRate,
    setSeekableEnd,
    setVolume,
    snowplowEvents,
    video
  ]);
};

export default useBindVideoEventListeners;
